/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { OrganizationService } from '@http/clients/organization.service';
import mapboxgl, { MapMouseEvent } from 'mapbox-gl';
import { Observable, Subject } from 'rxjs';
import { ICardDetails } from '../components/shared/map-card/map-card.component';
import { GlobalConfigurationService } from './global-configuration.service';
import { UserOrganizationStorageService } from './user-organization-storage.service';

export interface MapFeature {
    id?: string;
    latitude?: number;
    longitude?: number;
}

export interface PanelFieldConfig {
    mainInfo: Array<{
        label: string;
        valueKey?: string;
        valueFn?: (feature: any) => any;
        link?: { base: string; extra: string };
    }>;
    tooltip: Array<{
        label: string;
        valueKey?: string;
        valueFn?: (feature: any) => any;
    }>;
    routerLink: { base: string; extraKeys: any[] };
    salespersonsKey?: string;
}

@Injectable({
    providedIn: 'root'
})
export class MapService {
    private map!: mapboxgl.Map;
    private enableClusters: boolean;
    private featureListener!: string;
    private lastBounds: mapboxgl.LngLatBounds | null;
    private panelConfig?: PanelFieldConfig;

    private readonly defaultContainerId: string;
    private readonly defaultCenterCoordinates: [number, number];
    private readonly defaultZoomLevel: number;

    private readonly loadedSubject = new Subject<void>();
    public loaded$: Observable<void> = this.loadedSubject.asObservable();

    private readonly markerSelectedSubject = new Subject<MapFeature[]>();
    public markerSelected$: Observable<MapFeature[]> = this.markerSelectedSubject.asObservable();

    private readonly refreshSubject = new Subject<void>();
    public readonly refresh$ = this.refreshSubject.asObservable();

    private readonly panelDataSubject = new Subject<{ items: ICardDetails[]; count: number }>();
    public readonly panelData$ = this.panelDataSubject.asObservable();

    constructor(
        private readonly userOrganizationStorageService: UserOrganizationStorageService,
        private readonly organizationService: OrganizationService,
        private readonly globalConfigurationService: GlobalConfigurationService
    ) {
        this.enableClusters = true;
        this.defaultContainerId = 'map';
        this.defaultCenterCoordinates = [-103.5917, 40.6699];
        this.defaultZoomLevel = 4;
        this.lastBounds = null;
    }

    public setPanelConfig(config: PanelFieldConfig): void {
        this.panelConfig = config;
    }

    async initializeMap(entityToObserve: string, enableClusters = true, navigationControl = true): Promise<void> {
        try {
            const mapboxToken = this.globalConfigurationService.getKey('map Token');
            mapboxgl.accessToken = mapboxToken;

            const initialCenter = await this.getInitialCenterCoordinates();

            this.map = new mapboxgl.Map({
                container: this.defaultContainerId,
                style: 'mapbox://styles/mapbox/streets-v12',
                center: initialCenter,
                zoom: this.defaultZoomLevel,
                attributionControl: false
            });

            this.enableClusters = enableClusters;
            if (navigationControl) {
                this.addNavigationControl();
            }
            this.featureListener = entityToObserve;

            this.map.on('load', () => {
                this.addMapListeners();
                this.addCustomMarkerImage();

                this.lastBounds = this.map.getBounds();
                this.loadedSubject.next();
            });
        } catch (error) {
            console.error('Error initializing map:', error);
        }
    }

    getMapBounds(): mapboxgl.LngLatBounds | null {
        return this.map.getBounds();
    }

    fetchData(data: any[], sourceId: string): void {
        const geojsonData = this.createGeoJSONSource(data) as mapboxgl.GeoJSONSource;
        this.enableClusters ? this.addClustersLayers(sourceId, geojsonData) : this.addLayer(sourceId, geojsonData);
    }

    public shouldRefreshBounds(bounds: mapboxgl.LngLatBounds, lastBounds: mapboxgl.LngLatBounds): boolean {
        const south = bounds.getSouth(),
            north = bounds.getNorth();
        const west = bounds.getWest(),
            east = bounds.getEast();
        const lastSouth = lastBounds.getSouth(),
            lastNorth = lastBounds.getNorth();
        const lastWest = lastBounds.getWest(),
            lastEast = lastBounds.getEast();

        const insideLat = south >= lastSouth && north <= lastNorth;
        const insideLng = west >= lastWest && east <= lastEast;
        return !(insideLat && insideLng);
    }

    private async getInitialCenterCoordinates(): Promise<[number, number]> {
        const userOrganization = this.userOrganizationStorageService.getUserOrganization();
        if (userOrganization?.id) {
            try {
                const organizationInfo = await this.organizationService.getById(userOrganization.id);
                if (organizationInfo.longitude && organizationInfo.latitude) {
                    return [organizationInfo.longitude, organizationInfo.latitude];
                }
            } catch (error) {
                console.error(error);
            }
        }
        return this.defaultCenterCoordinates;
    }

    private addNavigationControl(): void {
        const nav = new mapboxgl.NavigationControl({ visualizePitch: true });
        this.map.addControl(nav, 'bottom-right');
    }

    private createGeoJSONSource(data: any[]): any {
        if (!data?.length) {
            return {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: []
                }
            };
        }

        return {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: data.map((p) => ({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [p.longitude, p.latitude]
                    },
                    properties: p
                }))
            },
            cluster: this.enableClusters,
            ...(this.enableClusters && { clusterMaxZoom: 14 }),
            ...(this.enableClusters && { clusterRadius: 50 })
        };
    }

    private checkLayerAndSource(layersIds: string[], sourceId: string, sourceData: mapboxgl.GeoJSONSource): void {
        if (this.map.getSource(sourceId)) {
            for (const id of layersIds) {
                if (this.map.getLayer(id)) {
                    this.map.removeLayer(id);
                }
            }
            this.map.removeSource(sourceId);
        }
        this.map.addSource(sourceId, sourceData as mapboxgl.GeoJSONSourceSpecification);
    }

    private addLayer(sourceId: string, geojsonData: mapboxgl.GeoJSONSource): void {
        const layersIds = [`${sourceId}-no-cluster-layer`];
        if (!layersIds.length) return;
        this.checkLayerAndSource(layersIds, sourceId, geojsonData);
        const layerId = layersIds[0];

        this.map.addLayer({
            id: layerId,
            type: 'symbol',
            source: sourceId,
            layout: {
                'icon-image': 'custom-marker-icon',
                'icon-size': 0.5
            }
        });

        this.addLayerListeners(layerId);
    }

    private addLayerListeners(layerId: string): void {
        this.map.on('click', layerId, (e) => {
            this.processMarkerClick(e);
        });

        this.map.on('mouseenter', layerId, () => {
            this.map.getCanvas().style.cursor = 'pointer';
        });

        this.map.on('mouseleave', layerId, () => {
            this.map.getCanvas().style.cursor = '';
        });
    }

    private addClustersLayers(sourceId: string, geojsonData: mapboxgl.GeoJSONSource): void {
        const layersIds = [
            `${sourceId}-cluster-layer`,
            `${sourceId}-cluster-count-layer`,
            `${sourceId}-unclustered-layer`
        ];
        if (!layersIds.length) return;
        this.checkLayerAndSource(layersIds, sourceId, geojsonData);

        this.map.addLayer({
            id: `${sourceId}-cluster-layer`,
            type: 'circle',
            source: sourceId,
            filter: ['has', 'point_count'],
            paint: {
                //   * #175C89, 20px circles when point count is less than 50
                //   * #5E6162, 30px circles when point count is between 50 and 100
                //   * #0095FF, 40px circles when point count is greater than or equal to 100
                'circle-color': ['step', ['get', 'point_count'], '#175C89', 50, '#5E6162', 100, '#0095FF'],
                'circle-radius': ['step', ['get', 'point_count'], 20, 50, 30, 100, 40]
            }
        });

        this.map.addLayer({
            id: `${sourceId}-cluster-count-layer`,
            type: 'symbol',
            source: sourceId,
            filter: ['has', 'point_count'],
            layout: {
                'text-field': ['get', 'point_count_abbreviated'],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12
            },
            paint: {
                'text-color': '#ffffff'
            }
        });

        this.map.addLayer({
            id: `${sourceId}-unclustered-layer`,
            type: 'symbol',
            source: sourceId,
            filter: ['!', ['has', 'point_count']],
            layout: {
                'icon-image': 'custom-marker-icon',
                'icon-size': 0.5
            }
        });

        this.addClustersLayersListeners(layersIds, sourceId);
    }

    private addMapListeners(): void {
        this.map.on('moveend', () => {
            const bounds = this.map.getBounds();
            if (!bounds) return;

            if (!this.lastBounds) {
                this.lastBounds = bounds;
                this.refreshSubject.next();
                return;
            }

            if (this.shouldRefreshBounds(bounds, this.lastBounds)) {
                this.lastBounds = bounds;
                this.refreshSubject.next();
            }
        });
    }

    private addClustersLayersListeners(layersIds: string[], sourceId: string): void {
        const clusterLayerId =
            layersIds.find((element) => element.includes('cluster') && !element.includes('count')) ?? layersIds[0];

        this.map.on('click', clusterLayerId, (e) => {
            const features = this.map.queryRenderedFeatures(e.point, {
                layers: [clusterLayerId]
            });

            if (!features) return;
            const feature = features[0];

            if (!feature?.properties || feature.geometry.type !== 'Point') return;
            const [longitude, latitude] = feature.geometry.coordinates;
            const clusterId = feature.properties['cluster_id'];

            const source = this.map.getSource(sourceId) as mapboxgl.GeoJSONSource;
            source.getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err) return;
                this.map.easeTo({
                    center: [longitude, latitude],
                    zoom: zoom ?? undefined
                });
            });
        });

        const unclusteredLayerId = layersIds.find((element) => element.includes('unclustered'));
        if (unclusteredLayerId) {
            this.map.on('click', unclusteredLayerId, (e) => {
                this.processMarkerClick(e);
            });
        }

        this.map.on('mouseenter', clusterLayerId, () => {
            this.map.getCanvas().style.cursor = 'pointer';
        });

        this.map.on('mouseleave', clusterLayerId, () => {
            this.map.getCanvas().style.cursor = '';
        });
    }

    private processMarkerClick(event: MapMouseEvent) {
        if (!event.features) return;
        const feature = event.features[0];
        if (!feature?.properties || feature.geometry.type !== 'Point') return;

        const parsedFeatures = JSON.parse(feature.properties[this.featureListener]) as MapFeature[];

        this.markerSelectedSubject.next(parsedFeatures);
        if (this.panelConfig) {
            const items = parsedFeatures.map((el: any) => ({
                id: el.id!,
                name: el.name,
                salespersons: this.panelConfig!.salespersonsKey
                    ? el[this.panelConfig!.salespersonsKey]
                    : el.salespersons,
                mainInfo: this.panelConfig!.mainInfo.map((cfg) => {
                    const value = cfg.valueFn ? cfg.valueFn(el) : el[cfg.valueKey!];
                    const field: any = { label: cfg.label, value };
                    if (cfg.link) {
                        field.link = `${cfg.link.base}/${el.id}${cfg.link.extra}`;
                    }
                    return field;
                }),
                tooltip: this.panelConfig!.tooltip.map((cfg) => {
                    const value = cfg.valueFn ? cfg.valueFn(el) : el[cfg.valueKey!];
                    return { label: cfg.label, value };
                }),
                routerLinkConfig: {
                    base: this.panelConfig!.routerLink.base,
                    extra: this.panelConfig!.routerLink.extraKeys
                }
            }));
            this.panelDataSubject.next({ items, count: items.length });
        }
    }

    private addCustomMarkerImage(): void {
        this.map.loadImage('../../assets/images/map-marker-point.png', (error, image) => {
            if (error) throw error;
            if (!this.map.hasImage('custom-marker-icon') && image) {
                this.map.addImage('custom-marker-icon', image as HTMLImageElement);
            }
        });
    }
}
